import AddressFormConfig from '../dynamic-form-config'
import USPSUtils from '../utils/usps-utils'
import {
  isString,
  isPlainObject,
  get,
  cloneDeep,
  pickBy,
} from 'lodash-es'

const addressFields = [
  'addressType',
  'firstName',
  'middleName',
  'lastName',
  'prefix',
  'suffix',
  'line1',
  'line2',
  'city',
  'stateCode',
  'postalCode',
  'countryCode',
]

const configToField = {
  addressOne: 'line1',
  addressTwo: 'line2',
  state_province: 'stateCode',
}

// US 50 states. (no territories)
const stateList = ['AK', 'AL', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY']
const pruneUSZipPlusFour = (zip) => {
  const usZipPlusFour = /^\d{5}(-\d{4})$/
  let pruned = zip
  if (usZipPlusFour.test(pruned)) {
    pruned = pruned.substr(0, pruned.indexOf('-'))
  }
  return pruned
}
const equalsIgnoreCase = (left, right) => (
  (isString(left) && isString(right))
    ? (left.toUpperCase() === right.toUpperCase())
    : (left === right)
)

const hasValue = string => (string !== undefined) && (string !== null) && (isString(string)) && (string !== '')

const exclude = (left, right) => {
  const reducd = []
  left.forEach((val) => {
    if (!right.some(r => val === r)) reducd.push(val)
  })
  return reducd
}

const getRequiredFields = (config) => {
  const fields = []
  Object.keys(config.fields).forEach((k) => {
    if (config.fields[k].required) {
      fields.push((configToField[k]) ? configToField[k] : k)
    }
  })
  return fields
}

class AddressUtils {

  /**
   * Determines if an address is not sufficient to provide EAD's for
   * @param {object} address address object
   */
  static isUnavailableEadAddress(address) {
    if (!address) throw new Error('method `isUnavailableEadAddress` argument required')
    let bool
    if (isPlainObject(address)) {
      bool = !stateList.some(s => get(address, 'stateCode', '') === s)
          || this.isInternationalAddress(get(address, 'countryCode', ''))
          || this.isMilitaryAddress(get(address, 'stateCode', ''))
    } else {
      throw new Error('method `isUnavailableEadAddress` requires an address object')
    }

    return bool
  }

  /**
   * Determines if the an address is a military address based on the provided state code
   * @param {string} stateCode The state code of the address
   * @return {boolean} If the state code is a military state code
   */
  static isMilitaryAddress(stateCode) {
    if (stateCode === undefined) throw new Error('method `isMilitaryAddress` argument required')
    if (!isString(stateCode)) throw new Error('method `isMilitaryAddress` requires argument of type string!')
    return stateCode === 'AP'
      || stateCode === 'AE'
      || stateCode === 'AA'
  }

  static isCanada(countryCode) {
    if (countryCode === undefined) throw new Error('method `isCanada` argument required')
    if (!isString(countryCode)) throw new Error('method `isCanada` requires argument of type string!')
    return countryCode === 'CA'
  }

  /**
   * Determines if an address is an international address based on the provided country code
   * @param {string} countryCode The country code of the address
   * @return {boolean} If the country code is outside the USA
   */
  static isInternationalAddress(countryCode) {
    if (countryCode === undefined) throw new Error('method `isInternationalAddress` argument required')
    if (!isString(countryCode)) throw new Error('method `isInternationalAddress` requires argument of type string!')
    return countryCode !== 'US'
  }

  static isSavedAddress(address) {
    if (address === undefined) throw new Error('method `isSavedAddress` argument required')
    return isString(address.reiRefId)
  }

  // create a new default address object
  static createDefault() {
    if (arguments.length > 0) throw new Error('method `createDefault` does not accept arguments!')
    return {
      acceptOffers: null,
      city: null,
      countryCode: null,
      email: null,
      firstName: null,
      isPayPalAddress: false,
      lastName: null,
      line1: null,
      line2: null,
      middleName: null,
      phoneNumber: null,
      postalCode: null,
      prefix: null,
      scrubStatus: 'NOT_SCRUBBED',
      stateCode: null,
      suffix: null,
    }
  }

  static createAddress(payload) {
    if (!isPlainObject(payload)) throw new Error('method `createAddress` requires argument of type object!')

    const address = payload.address || {}
    return {
      reiRefId: (payload.reiRefId) ? payload.reiRefId : null,
      address: {
        firstName: address.firstName,
        prefix: address.prefix,
        lastName: address.lastName,
        suffix: address.suffix,
        line1: address.line1,
        line2: address.line2,
        city: address.city,
        stateCode: address.stateCode,
        postalCode: address.postalCode,
        countryCode: address.countryCode,
        phoneNumber: address.phoneNumber,
        scrubStatus: address.scrubStatus,
        storeId: null,
      },
    }
  }

  // matches two address objects
  static isMatch(left, right, ignores) {
    if (!isPlainObject(left) || !isPlainObject(right)) throw new Error('method `isMatch`, left and right arguments must be objects!')
    if (ignores && !Array.isArray(ignores)) throw new Error('method `isMatch` requires `ignores` to be an array!')

    // If the reiRefId's match then we don't check anything else.
    if (left.reiRefId && left.reiRefId === right.reiRefId) return true

    const leftAddress = cloneDeep(left.address)
    const rightAddress = cloneDeep(right.address)

    // prune empty fields
    const l = pickBy(leftAddress, field => !!field)
    const r = pickBy(rightAddress, field => !!field)

    // prune Zip+4 -> Zip
    l.postalCode = pruneUSZipPlusFour(l.postalCode)
    r.postalCode = pruneUSZipPlusFour(r.postalCode)

    // remove the ignore fields
    const fieldsToCheck = ignores ? exclude(addressFields, ignores) : addressFields
    return fieldsToCheck.every(field => l[field] === r[field])
  }

  // matches two address objects (ignoring string case)
  static isMatchIgnoreCase(left, right, ignores) {
    if (!isPlainObject(left) || !isPlainObject(right)) throw new Error('method `isMatchIgnoreCase`, left and right arguments must be objects!')
    if (ignores && !Array.isArray(ignores)) throw new Error('method `isMatchIgnoreCase` requires `ignores` to be an array!')

    /* @fixme Apparently we should not rely on the reiRefId because of buggy address updating
       behavior, the accounts team is working on cleaning this up so hopefully at one point we can
       reable this. */
    // If the reiRefId's match then we don't check anything else.
    // if (left.reiRefId && left.reiRefId === right.reiRefId) return true;

    const leftAddress = cloneDeep(left.address)
    const rightAddress = cloneDeep(right.address)

    // prune empty fields
    const l = pickBy(leftAddress, field => !!field)
    const r = pickBy(rightAddress, field => !!field)

    // prune Zip+4 -> Zip
    l.postalCode = pruneUSZipPlusFour(l.postalCode)
    r.postalCode = pruneUSZipPlusFour(r.postalCode)

    // remove the ignore fields
    const fieldsToCheck = ignores ? exclude(addressFields, ignores) : addressFields
    return fieldsToCheck.every(field => equalsIgnoreCase(l[field], r[field]))
  }

  static createSrAddressString(address) {
    if (address === undefined) throw new Error('method `createSrAddressString` argument required')
    if (!isPlainObject(address)) throw new Error('method `createSrAddressString` an object')
    const fields = []
    fields.push(address.line1)
    if (address.line2) fields.push(address.line2)
    fields.push(address.city)
    fields.push(address.stateCode)
    fields.push(address.postalCode)
    return fields.join(' ')
  }

  static isNotEmpty(address, requiredFields) {
    if (!address) throw new Error('method `isNotEmpty` argument required')
    if (requiredFields && !Array.isArray(requiredFields)) throw new Error('method `isNotEmpty` requires `requiredFields` to be an array!')

    const required = requiredFields || [
      'city',
      'stateCode',
      'countryCode',
      'postalCode',
      'line1',
    ]

    // make sure the address contains the required fields
    // and that they are not null or undefined
    return required.some(value => get(address, value) != null)
  }

  static isScrubbed(address) {
    if (!address) throw new Error('method `isScrubbed` argument required')
    if (!AddressUtils.isReadyForScrubbing(address)) return false
    return (address.scrubStatus) &&
            (address.scrubStatus !== USPSUtils.SCRUBBED_VALUES.invalid) &&
            (address.scrubStatus !== USPSUtils.SCRUBBED_VALUES.unscrubbed)
  }

  static isReadyForScrubbing(address) {
    if (!address) throw new Error('method `isReadyForScrubbing` argument required')

    let requiredFields = null
    if (AddressUtils.isMilitaryAddress(get(address, 'stateCode') || '')) {
      requiredFields = getRequiredFields(AddressFormConfig.MILITARY)
    } else if (AddressUtils.isInternationalAddress(get(address, 'countryCode') || '')
                && !AddressUtils.isCanada(get(address, 'countryCode') || '')) {
      requiredFields = getRequiredFields(AddressFormConfig.INTERNATIONAL)
    } else if (AddressUtils.isCanada(get(address, 'countryCode') || '')) {
      requiredFields = getRequiredFields(AddressFormConfig.CANADA)
    } else {
      requiredFields = getRequiredFields(AddressFormConfig.US)
    }
    return requiredFields.every(key => hasValue(address[key]))
  }

  static createShippingAddressPayload(finalAddress) {
    if (!finalAddress) throw new Error('no appropriate shipping address found. Aborting auto-apply.')
    let payload = {}
    if (isPlainObject(finalAddress)) {
      payload = { shippingAddress: finalAddress }
    }
    return payload
  }
}

export default AddressUtils
